<template>
  <a-layout-header
    class="flex items-center justify-between"
    :style="{ height: '48px', padding: '0 16px', background: 'white' }"
  >
    <div class="pl-2.5 flex items-center w-full">
      <span
        class="text-base font-bold cursor-pointer inline-block truncate w-[calc(100vw-6rem)] md:w-[calc(280px-0.625rem)]"
        @click="
          () => {
            router.push('/login');
          }
        "
      >
      <Logo />
    </span>
    </div>
  </a-layout-header>
  <a-spin :spinning="spinning">
    <div class="text-xl fixed inset-0 flex items-center justify-center" v-if="token && !tokenOk">
      <div class="max-w-lg mx-auto text-center">
        <div class="flex items-center justify-center mb-4">
          <img :src="sadFaceIcon" alt="" class="w-[60%]" />
        </div>
        <div>
          <p class="font-bold text-2xl mb-2">トークンが無効か期限切れです</p>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8 login-form"
    >
      <div class="sm:mx-auto sm:w-full sm:max-w-sm">
        <div class="text-xl font-bold text-center text-gray-500">{{ $t('reset_pass') }}</div>
      </div>
      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <a-form class="space-y-7" :model="state" @finish="resetPass">
          <div>
            <div class="mt-2 text-center block text-xs text-gray-400">
              <span
                v-html="
                  isSendMailScreen
                    ? $t('reset_pass_suggest_info')
                    : $t('reset_pass_suggest_info_rule')
                "
              >
              </span>
            </div>
          </div>

          <div v-if="isSendMailScreen">
            <div class="mt-2">
              <a-form-item
                name="email"
                :rules="[{ trigger: ['blur', 'change'], validator: emailValidator }]"
              >
                <a-input
                  name="email"
                  ref="emailInput"
                  maxlength="254"
                  :placeholder="$t('your_registered_email_address')"
                  class="block w-full sm:text-sm sm:leading-6"
                  v-model:value="state.email"
                >
                </a-input>
              </a-form-item>
            </div>
          </div>

          <div v-if="!isSendMailScreen">
            <div class="mt-2">
              <a-form-item
                name="newPassword"
                :rules="[{ validator: validatePassword, trigger: ['blur', 'change'] }]"
              >
                <a-input-password
                  name="newpassword"
                  maxlength="24"
                  ref="newPasswordInput"
                  :placeholder="$t('new_password')"
                  class="block w-full sm:text-sm sm:leading-6"
                  v-model:value="state.newPassword"
                >
                </a-input-password>
              </a-form-item>
            </div>
          </div>

          <div v-if="!isSendMailScreen">
            <div class="mt-2">
              <a-form-item
                name="newPasswordConf"
                :rules="[{ validator: validatePasswordConf, trigger: ['blur', 'change'] }]"
              >
                <a-input-password
                  name="newpasswordConf"
                  maxLength="24"
                  :placeholder="$t('confirm_password')"
                  class="block w-full sm:text-sm sm:leading-6"
                  v-model:value="state.newPasswordConf"
                >
                </a-input-password>
              </a-form-item>
            </div>
          </div>

          <div>
            <a-button
              type="primary"
              html-type="submit"
              class="flex w-full justify-center submit-but"
            >
              {{ isSendMailScreen ? $t('submit') : $t('update_data') }}
            </a-button>
          </div>

          <div v-if="isSendMailScreen" class="mt-10 text-center text-sm space-y-4">
            <a @click="router.push('/login')" class="back-login">{{ $t('back_to_login') }}</a>
          </div>
        </a-form>
      </div>
    </div>
  </a-spin>
</template>
<script setup lang="ts">
import { nextTick, onBeforeMount, onMounted, reactive, ref } from 'vue';
import Logo from '@/components/Logo.vue';
import { message, Modal } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores';
import type { Rule } from 'ant-design-vue/es/form';
import sadFaceIcon from '@/assets/images/sad_face_icon.png';
import { checkTokenInMail } from '@/services';
import { setApiDisabled } from '@/services/http';
import { formatModalTitle } from '@/utils/modules/message';

const authStore = useAuthStore();
const { t } = useI18n();
const router = useRouter();
const state = reactive({
  email: '',
  newPassword: '',
  newPasswordConf: '',
  token: ''
});
const isSendMailScreen = ref(true);
const token = ref();
const tokenOk = ref(true);
const spinning = ref(false);
const emailInput = ref<HTMLInputElement | null>(null);
const newPasswordInput = ref<HTMLInputElement | null>(null);
onBeforeMount(async () => {
  setApiDisabled(false);
  token.value = router.currentRoute.value.query.token;
  if (token.value) {
    spinning.value = true;
    isSendMailScreen.value = false;
    state.token = token.value;
    checkTokenInMail(token.value)
      .then((res: any) => {
        if (res.result) {
          tokenOk.value = true;
        } else {
          tokenOk.value = false;
        }
      })
      .catch(() => {
        tokenOk.value = false;
      })
      .finally(() => {
        spinning.value = false;
      });
  }
});
onMounted(() => {
  nextTick(() => {
    emailInput.value?.focus();
    newPasswordInput.value?.focus();
  });
});
const emailValidator = (rule: Rule, value: string) => {
  const mailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) {
    return Promise.reject(new Error(t('MSG_00_21')));
  } else if (!mailRegex.test(value)) {
    return Promise.reject(new Error(t('email_invalid')));
  } else {
    return Promise.resolve();
  }
};
const validatePassword = (rule: Rule, value: string) => {
  const halfWidthRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
  if (!value) {
    return Promise.reject(new Error(t('MSG_00_21')));
  } else if (!halfWidthRegex.test(value)) {
    return Promise.reject(new Error(t('MSG_03_1')));
  } else {
    return Promise.resolve();
  }
};
const validatePasswordConf = (rule: Rule, value: string) => {
  if (!value) {
    return Promise.reject(new Error(t('MSG_00_21')));
  } else if (value != state.newPassword) {
    return Promise.reject(new Error(t('MSG_03_2')));
  } else {
    return Promise.resolve();
  }
};

const sendResetPassEmail = () => {
  spinning.value = true;
  authStore
    .sendMail(state.email)
    .then((res: any) => {
      if (res.result) {
        message.info({
          content: t('MSG_02_2').replace('[email]', state.email),
          class: 'login-info'
        });
      } else {
        message.error(t('MSG_02_1'));
      }
    })
    .catch(() => {
      message.error(t('server_error'));
    })
    .finally(() => {
      spinning.value = false;
    });
};

const sendNewPassSetting = () => {
  spinning.value = true;
  authStore
    .resetPass(state.newPassword, state.token)
    .then((res: any) => {
      if (res.result) {
        router.push('/login');
        message.success(t('MSG_00_02'));
      } else {
        message.error(t('invalid_request'));
      }
    })
    .catch(() => {
      message.error(t('server_error'));
    })
    .finally(() => {
      spinning.value = false;
    });
};

const resetPass = () => {
  let modal = Modal.confirm({
    title: '',
    okType: 'danger',
    okText: t('cancel_button'),
    cancelText: t('yes'),
    centered: true,
    maskClosable: true,
    cancelButtonProps: {
      type: 'primary',
      onClick: async () => {
        isSendMailScreen.value ? sendResetPassEmail() : sendNewPassSetting();
        modal.destroy();
      }
    }
  });
  const title = isSendMailScreen.value ? t('MSG_02_3') : t('MSG_00_05');
  modal.update({
    title: formatModalTitle(title)
  });
};
</script>
<style lang="scss">
@import '@/assets/styles/variables.scss';

.login-form {
  .submit-but {
    background-color: $blue-color;
  }

  .back-login {
    color: rgb(96 165 250) !important;
  }
}

.login-info {
  white-space: pre-wrap;
}
</style>
